import { useToast } from '@app/core/notifications';
import { useFragmentsViewCapture } from '@app/modules/fragments/hooks/useFragmentsViewCapture';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { webApi } from '@mi-front/api-clients';
import { useRouter } from '@mi-front/core';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import { useFragment } from '../../hooks/useFragment';

const cloneSuccessMessage = defineMessage({
  id: 'fragments.cloneScreen.cloned',
  defaultMessage: 'Fragmento clonado',
});

const cloneErrorMessage = defineMessage({
  id: 'fragments.cloneScreen.cloneError',
  defaultMessage: 'No se pudo clonar el fragmento',
});

const cloneDialogTitleLabel = defineMessage({
  id: 'fragments.cloneScreen.title',
  defaultMessage: 'Clonar fragmento',
});

const cloneTitleFieldLabel = defineMessage({
  id: 'fragments.cloneScreen.inputLabel',
  defaultMessage: 'Nuevo titulo',
});

const cloneTitleFieldRequiredLabel = defineMessage({
  id: 'fragments.cloneScreen.inputRequired',
  defaultMessage: 'El titulo es obligatorio',
});

/** Suggested clone title: source title plus localized “copy” suffix ({title} = original fragment title). */
const cloneDefaultTitleMessage = defineMessage({
  id: 'fragments.cloneScreen.defaultTitle',
  defaultMessage: '{title} (copia)',
});

/**
 * Clone fragment action with a title dialog and mutation handling.
 */
export function FragmentsCloneView() {
  const { fragment } = useFragment();
  const { history } = useRouter();
  const { $t, formatMessage } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const fragmentsCapture = useFragmentsViewCapture();
  const [open, setOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(() =>
    formatMessage(cloneDefaultTitleMessage, { title: fragment.title }),
  );
  const [titleError, setTitleError] = useState(false);
  const cloneFragmentMutation = webApi.useCloneFragmentDetail();

  const isCloning = cloneFragmentMutation.isPending;

  function handleOpen() {
    setNewTitle(formatMessage(cloneDefaultTitleMessage, { title: fragment.title }));
    setTitleError(false);
    setOpen(true);
  }

  function handleClose() {
    if (!isCloning) {
      setOpen(false);
    }
  }

  async function handleClone() {
    const title = newTitle.trim();

    if (!title) {
      setTitleError(true);
      return;
    }

    try {
      const response = await cloneFragmentMutation.mutateAsync({
        id: fragment.id,
        data: { title },
      });

      fragmentsCapture.clone({
        fragmentId: fragment.id,
        clonedFragmentId: response.id,
      });
      notifySuccess($t(cloneSuccessMessage));
      setOpen(false);
      history.push(routeGenerator.view(response.id));
    } catch {
      notifyError($t(cloneErrorMessage));
    }
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        onClick={handleOpen}
        startIcon={<ContentCopyIcon />}
      >
        <FormattedMessage id="common.actions.clone" defaultMessage="Clonar" />
      </Button>

      <Dialog maxWidth="sm" fullWidth open={open} onClose={handleClose}>
        <DialogTitle>{$t(cloneDialogTitleLabel)}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            required
            margin="normal"
            value={newTitle}
            disabled={isCloning}
            onChange={(event) => {
              setNewTitle(event.target.value);
              if (titleError && event.target.value.trim()) {
                setTitleError(false);
              }
            }}
            label={$t(cloneTitleFieldLabel)}
            error={titleError}
            helperText={titleError ? $t(cloneTitleFieldRequiredLabel) : undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isCloning}>
            <FormattedMessage id="common.actions.cancel" defaultMessage="Cancelar" />
          </Button>
          <LoadingButton onClick={handleClone} loading={isCloning} variant="contained">
            <FormattedMessage id="common.actions.clone" defaultMessage="Clonar" />
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
